import { useState } from 'react';
import { Outlet, NavLink, Link, useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { motion } from 'motion/react';
import {
  LayoutDashboard,
  Package,
  ClipboardList,
  Store,
  Share2,
  CreditCard,
  Settings,
  Menu,
  X,
  LogOut,
  ExternalLink,
  Copy,
  Check,
  ShieldCheck,
} from 'lucide-react';

import { LanguageSwitcher } from '@/components/common/LanguageSwitcher';
import { ThemeToggle } from '@/components/common/ThemeToggle';
import { Wordmark } from '@/components/common/Wordmark';
import { Avatar } from '@/components/ui/Avatar';
import { Badge } from '@/components/ui/Badge';
import { useAuthStore } from '@/stores/authStore';
import { logoutOwner } from '@/services/authService';
import { useNewOrderNotifier } from '@/hooks/useNewOrderNotifier';
import { hasActiveAccess } from '@/lib/access';
import { env } from '@/config/env';
import { cn } from '@/lib/utils';
import type { SubscriptionStatus } from '@/types/owner';

const NAV_GROUPS = [
  {
    labelKey: 'nav.sectionAtelier',
    items: [
      { to: '/dashboard', icon: LayoutDashboard, key: 'nav.overview', end: true },
      { to: '/dashboard/products', icon: Package, key: 'nav.products' },
      { to: '/dashboard/orders', icon: ClipboardList, key: 'nav.orders' },
    ],
  },
  {
    labelKey: 'nav.sectionCroissance',
    items: [
      { to: '/dashboard/share', icon: Share2, key: 'nav.share' },
      { to: '/dashboard/billing', icon: CreditCard, key: 'nav.billing' },
    ],
  },
  {
    labelKey: 'nav.sectionCompte',
    items: [
      { to: '/dashboard/profile', icon: Store, key: 'nav.profile' },
      { to: '/dashboard/settings', icon: Settings, key: 'nav.settings' },
    ],
  },
];

const STATUS_TONES: Record<SubscriptionStatus, 'neutral' | 'primary' | 'success' | 'warning' | 'danger' | 'info'> = {
  active: 'success',
  trialing: 'info',
  past_due: 'warning',
  unpaid: 'danger',
  canceled: 'neutral',
  incomplete: 'warning',
  inactive: 'neutral',
};

const PAGE_TITLES: Record<string, string> = {
  '/dashboard': 'nav.overview',
  '/dashboard/products': 'nav.products',
  '/dashboard/orders': 'nav.orders',
  '/dashboard/share': 'nav.share',
  '/dashboard/billing': 'nav.billing',
  '/dashboard/profile': 'nav.profile',
  '/dashboard/settings': 'nav.settings',
};

export function DashboardShell() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const owner = useAuthStore((s) => s.owner);
  const user = useAuthStore((s) => s.user);
  const isAdmin = useAuthStore((s) => s.isAdmin);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [signingOut, setSigningOut] = useState(false);
  const [copied, setCopied] = useState(false);
  useNewOrderNotifier(owner?.ownerId);

  const publicUrl = owner?.shopSlug ? `${env.publicStoreBaseUrl}/${owner.shopSlug}` : '';
  const pageTitleKey =
    PAGE_TITLES[location.pathname] ??
    (location.pathname.startsWith('/dashboard/orders/') ? 'nav.orders' : 'nav.overview');

  async function handleLogout() {
    try {
      setSigningOut(true);
      await logoutOwner();
      navigate('/', { replace: true });
    } catch (err) {
      toast.error(t('errors.genericError'), {
        description: err instanceof Error ? err.message : undefined,
      });
    } finally {
      setSigningOut(false);
    }
  }

  async function copyUrl() {
    if (!publicUrl) return;
    try {
      await navigator.clipboard.writeText(publicUrl);
      setCopied(true);
      toast.success(t('common.copied'));
      setTimeout(() => setCopied(false), 1800);
    } catch {
      toast.error(t('errors.genericError'));
    }
  }

  return (
    <div className="flex h-screen overflow-hidden bg-bg">
      {/* SIDEBAR */}
      <aside
        className={cn(
          'fixed inset-y-0 left-0 z-50 flex w-72 transform flex-col border-r border-border bg-card transition-transform duration-200 md:static md:translate-x-0',
          mobileOpen ? 'translate-x-0' : '-translate-x-full',
        )}
        aria-label="Sidebar"
      >
        <div className="flex items-center justify-between px-5 pb-2 pt-6">
          <Link to="/dashboard">
            <Wordmark />
          </Link>
          <button
            type="button"
            onClick={() => setMobileOpen(false)}
            className="cursor-pointer rounded-full p-2 text-muted transition-colors hover:bg-faint md:hidden"
            aria-label={t('common.close')}
          >
            <X size={18} />
          </button>
        </div>

        {owner && (
          <div className="mx-3 mt-5 rounded-2xl border border-border bg-faint p-3">
            <div className="flex items-center gap-3">
              <Avatar src={owner.logoUrl} name={owner.shopName} size={40} />
              <div className="min-w-0 flex-1">
                <p className="truncate text-sm font-medium">{owner.shopName}</p>
                <p className="truncate font-mono text-[10px] text-muted">
                  /{owner.shopSlug}
                </p>
              </div>
              <Badge tone={STATUS_TONES[owner.subscriptionStatus]}>
                {hasActiveAccess(owner) ? '●' : '○'}
              </Badge>
            </div>
          </div>
        )}

        <nav className="flex-1 space-y-5 overflow-y-auto px-3 py-4">
          {NAV_GROUPS.map((group) => (
            <div key={group.labelKey}>
              <p className="px-3 text-[10px] font-medium uppercase tracking-[0.18em] text-muted/80">
                {t(group.labelKey)}
              </p>
              <div className="mt-2 space-y-0.5">
                {group.items.map(({ to, icon: Icon, key, end }) => (
                  <NavLink
                    key={to}
                    to={to}
                    end={end}
                    onClick={() => setMobileOpen(false)}
                    className={({ isActive }) =>
                      cn(
                        'relative flex items-center gap-3 rounded-xl px-3 py-2.5 text-sm font-medium transition-all duration-300 ease-soft',
                        isActive
                          ? 'bg-primary/[0.1] text-primary'
                          : 'text-muted hover:bg-faint hover:text-ink',
                      )
                    }
                  >
                    {({ isActive }) => (
                      <>
                        {isActive && (
                          <motion.span
                            layoutId="active-bar"
                            className="absolute inset-y-0 left-0 my-auto h-5 w-1 rounded-r-full bg-primary"
                            transition={{ type: 'spring', stiffness: 400, damping: 35 }}
                          />
                        )}
                        <Icon size={18} />
                        {t(key)}
                      </>
                    )}
                  </NavLink>
                ))}
              </div>
            </div>
          ))}

          {isAdmin && (
            <div>
              <p className="px-3 text-[10px] font-medium uppercase tracking-[0.18em] text-muted/80">
                {t('admin.title')}
              </p>
              <div className="mt-2">
                <NavLink
                  to="/admin"
                  onClick={() => setMobileOpen(false)}
                  className={({ isActive }) =>
                    cn(
                      'relative flex items-center gap-3 rounded-xl px-3 py-2.5 text-sm font-medium transition-all duration-300 ease-soft',
                      isActive
                        ? 'bg-primary/[0.1] text-primary'
                        : 'text-muted hover:bg-faint hover:text-ink',
                    )
                  }
                >
                  {({ isActive }) => (
                    <>
                      {isActive && (
                        <motion.span
                          layoutId="active-bar"
                          className="absolute inset-y-0 left-0 my-auto h-5 w-1 rounded-r-full bg-primary"
                          transition={{ type: 'spring', stiffness: 400, damping: 35 }}
                        />
                      )}
                      <ShieldCheck size={18} />
                      {t('nav.admin')}
                    </>
                  )}
                </NavLink>
              </div>
            </div>
          )}
        </nav>

        <div className="mx-3 mb-4 rounded-2xl border border-border bg-faint p-3">
          <div className="flex items-center gap-2.5">
            <Avatar name={user?.displayName ?? user?.email ?? '?'} size={32} />
            <div className="min-w-0 flex-1">
              <p className="truncate text-xs font-medium">
                {user?.displayName ?? owner?.fullName ?? t('common.appName')}
              </p>
              <p className="truncate text-[10px] text-muted">{user?.email}</p>
            </div>
            <button
              type="button"
              onClick={handleLogout}
              disabled={signingOut}
              className="cursor-pointer rounded-full p-2 text-muted transition-colors hover:bg-card hover:text-danger disabled:opacity-50"
              aria-label={t('settings.logout')}
              title={t('settings.logout')}
            >
              <LogOut size={14} />
            </button>
          </div>
        </div>
      </aside>

      {mobileOpen && (
        <button
          type="button"
          aria-label={t('common.close')}
          className="fixed inset-0 z-40 bg-ink/40 backdrop-blur-sm md:hidden"
          onClick={() => setMobileOpen(false)}
        />
      )}

      <div className="flex min-w-0 flex-1 flex-col overflow-hidden">
        {/* TOPBAR */}
        <header className="sticky top-0 z-30 border-b border-border/70 bg-bg/75 backdrop-blur-xl">
          <div className="flex h-[4.5rem] items-center justify-between gap-4 px-4 sm:px-6 lg:px-8">
            <div className="flex items-center gap-3">
              <button
                type="button"
                className="cursor-pointer rounded-full p-2 text-muted transition-colors hover:bg-faint md:hidden"
                onClick={() => setMobileOpen(true)}
                aria-label="Menu"
              >
                <Menu size={20} />
              </button>
              <div>
                <p className="text-[0.62rem] font-medium uppercase tracking-[0.2em] text-muted">
                  {t('nav.overview') === t(pageTitleKey)
                    ? t('common.appName')
                    : t('nav.overview')}
                </p>
                <p className="display text-2xl leading-none">{t(pageTitleKey)}</p>
              </div>
            </div>

            <div className="hidden items-center gap-2 lg:flex">
              {publicUrl && (
                <div className="flex items-center gap-1.5 rounded-full border border-border bg-card pl-3 pr-1 py-1">
                  <span className="font-mono text-xs text-muted">
                    {publicUrl.replace(/^https?:\/\//, '')}
                  </span>
                  <button
                    type="button"
                    onClick={copyUrl}
                    className="grid h-7 w-7 place-items-center rounded-full text-muted hover:bg-bg"
                    aria-label={t('common.copy')}
                    title={t('common.copy')}
                  >
                    {copied ? <Check size={12} /> : <Copy size={12} />}
                  </button>
                  <a
                    href={publicUrl}
                    target="_blank"
                    rel="noreferrer"
                    className="grid h-7 w-7 place-items-center rounded-full bg-ink text-bg hover:bg-ink/85"
                    aria-label={t('common.share')}
                    title="Open"
                  >
                    <ExternalLink size={12} />
                  </a>
                </div>
              )}
            </div>

            <div className="flex items-center gap-2">
              <LanguageSwitcher />
              <ThemeToggle />
            </div>
          </div>
        </header>

        <main className="flex-1 overflow-y-auto px-4 py-8 sm:px-6 lg:px-8">
          <div className="mx-auto max-w-7xl">
            <Outlet />
          </div>
        </main>
      </div>
    </div>
  );
}
